import React, { useId, useRef, useState, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  ColumnFiltersState,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  VisibilityState,
} from '@tanstack/react-table';

import Button from 'design-system/src/components/Button/Button';
import type { NotificationRow } from '../../api';
import {
  sortOfficeNames,
  getDropdownInitialValueFromSearchParams,
  getDropdownInitialValueFromSearchParamsAndCookie,
  getInitialTableColumnFilterValueFromCookie,
  updateTableColumnFilterValueCookie,
  notificationsFilterValuesFromSearchParams,
  updateUrlSearchParams,
  getPrescriberFilterValue,
} from '../../utils';
import { stringsToDropdownOptions } from 'shared-utils/utils/dropdownFilter';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { MULTI_SELECT_DELINEATOR, MIN_SEARCH_LENGTH } from '../../constants';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';
import { TableMultiselectFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import type {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { AuthContext } from '../../auth';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  FilterBarRow,
  TabsAndSearchRow,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  HeaderSortingIndicator,
  TableTabList,
  TableResultCount,
  SearchResultsBanner,
  DividerPipe,
  NoPaddingCheckbox,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
  DateCellWithStatus,
} from '../Table';
import { TableSearchWithPreview } from '../TableSearchWithPreview';
import { useSearchNotifications } from './useSearchNotifications';
import { NotificationPreviewItem } from './NotificationPreviewItem';
import { Typography } from 'design-system/src/components/Typography/Typography';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../../api';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from 'design-system/src/components/SaveFiltersButton/SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { notificationsQueryKey } from '../../queries';
import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import FormattedCell from 'design-system/src/components/FormattedCell/FormattedCell';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';
import { createTrackingProps } from 'shared-utils/analytics';

const columnHelper = createColumnHelper<NotificationRow>();

interface NotificationsTableProps {
  data: NotificationRow[];
}

const taskDateSortingFn = (a: Row<NotificationRow>, b: Row<NotificationRow>, columnId: string) => {
  return new Date(b.getValue(columnId)).getTime() - new Date(a.getValue(columnId)).getTime();
};

const taskPatientSortingFn = (a: Row<NotificationRow>, b: Row<NotificationRow>, columnId: string) => {
  const aValue = a.getValue(columnId) as { name: string; dob: string };
  const bValue = b.getValue(columnId) as { name: string; dob: string };
  return bValue.name.localeCompare(aValue.name);
};

const taskTypeSortingFn = (a: Row<NotificationRow>, b: Row<NotificationRow>, columnId: string) => {
  const aValue = a.getValue(columnId) as { type: string; drugDescription: string; context: string };
  const bValue = b.getValue(columnId) as { type: string; drugDescription: string; context: string };
  return bValue.type.localeCompare(aValue.type);
};

const taskTableMultiSelectFilter = (row: Row<NotificationRow>, columnId: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // drug_description requires special handling because we're looking for a substring match
  // whereas the other columns are looking for an exact match
  if (columnId === 'drug_description') {
    const drugDescription = row.original.drug_description?.toLowerCase() || '';
    return filterValue.some((filterVal) => drugDescription.includes(filterVal.toLowerCase()));
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

const TopAlignedTableCell = styled(TableCell)`
  vertical-align: top;
`;

const getDefaultNullStateType = (status: string) => {
  if (status === 'completed') {
    return TableNullStateType.NO_NOTIFICATIONS_COMPLETED;
  } else {
    return TableNullStateType.NO_NOTIFICATIONS_OPEN;
  }
};

const NotificationsTable: React.FC<NotificationsTableProps> = ({ data }) => {
  const navigate = useNavigate();
  const { notificationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const { data: filterOptions } = useFetchPortalTableFilters();
  const [selectedNotifications, setSelectedNotifications] = React.useState<string[]>([]);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const queryClient = useQueryClient();
  const statusTabId = useId();
  const statusPanelId = useId();

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const typeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();
  const officeCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'office_name'),
    [authContext.user?.member_id],
  );
  const drugCookieValues = React.useMemo(
    () => getInitialTableColumnFilterValueFromCookie(authContext.user?.member_id || null, 'drug_name'),
    [authContext.user?.member_id],
  );
  const hasUserClearedFilters = React.useRef(false);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableRows = table.getRowModel().rows.filter((row) => row.original.status !== 'completed');
      const selectableIds = selectableRows.map((row) => row.original.id);
      setSelectedNotifications(selectableIds);
    } else {
      setSelectedNotifications([]);
    }
  };

  const closeNotificationsMutation = useMutation<{ success: boolean }, AxiosError<{ error: string }>>({
    mutationFn: async () => {
      if (isTrainingMode) {
        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      return client
        .post('portal-notifications/close', { notifications: selectedNotifications })
        .then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationsQueryKey() });
      toast.custom(() => <Toast variant="success">Notifications marked as read</Toast>);
      setSelectedNotifications([]);
    },
    onError: async (error) => {
      await queryClient.invalidateQueries({ queryKey: notificationsQueryKey() });
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
      setSelectedNotifications([]);
    },
  });

  const statusParam = searchParams.get('status');
  const initialStatus = statusParam === 'open' || statusParam === 'completed' ? statusParam : 'open';
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    { id: 'status', value: initialStatus },
  ]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const typeStrings: string[] = [];
  data.forEach((task) => {
    if (task.task_type) {
      typeStrings.push(task.task_type);
    }
  });

  const handleCheckboxChange = (notificationId: string) => {
    setSelectedNotifications((prev) => {
      if (prev.includes(notificationId)) {
        return prev.filter((id) => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  const handleMarkAsComplete = () => {
    closeNotificationsMutation.mutate();
  };

  const columns = [
    columnHelper.accessor('id', {
      id: 'selection',
      header: () => {
        const selectableRows = table.getRowModel().rows.filter((row) => row.original.status !== 'completed');
        const allSelected =
          selectableRows.length > 0 && selectableRows.every((row) => selectedNotifications.includes(row.original.id));
        const isDisabled = table.getColumn('status')?.getFilterValue() === 'completed';

        return (
          <NoPaddingCheckbox
            checked={allSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            disabled={isDisabled}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
      cell: (info) => {
        return (
          <NoPaddingCheckbox
            checked={selectedNotifications.includes(info.getValue())}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(info.getValue());
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={table.getColumn('status')?.getFilterValue() === 'completed'}
          />
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 42,
    }),
    columnHelper.accessor('created_at', {
      header: 'Date',
      sortingFn: taskDateSortingFn,
      cell: (info) => (
        <DateCellWithStatus
          date={info.getValue()}
          statusLabel={appliedSearch && info.row.original.status === 'completed' ? 'Closed' : undefined}
        />
      ),
      size: DATE_COLUMN_SIZE,
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: taskPatientSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`DOB: ${formatStandardDate(info.getValue().dob)}`}
          />
        ),
        size: DEFAULT_COLUMN_SIZE,
      },
    ),
    columnHelper.accessor(
      (row) => {
        return { type: row.task_type, context: row.extra_context, drugDescription: row.drug_description };
      },
      {
        id: 'notification',
        header: 'Notification',
        sortingFn: taskTypeSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().type}
            subtitle={info.getValue().drugDescription}
            body={info.getValue().context}
          />
        ),
        filterFn: (row, _, filterValue) => {
          if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
          return filterValue.includes(row.original.task_type);
        },
        size: 600,
      },
    ),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      filterFn: taskTableMultiSelectFilter,
      size: DEFAULT_COLUMN_SIZE,
    }),

    // Status column - used for filtering, only displayed when search is active
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => (info.getValue() === 'queued' ? 'Open' : 'Completed'),
      filterFn: (row, _, filterValue) => {
        // 'all' bypasses filtering - used when search is active to show results across tabs
        if (filterValue === 'all') {
          return true;
        }
        if (filterValue == 'open') {
          return row.original.status == 'queued';
        } else if (filterValue == 'completed') {
          return row.original.status == 'completed';
        }
        return true;
      },
      enableGlobalFilter: false,
      size: 100,
    }),
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
    // drug_description is included in the 'notification' column, but it's easier to also give it its own column
    // for filtering purposes
    columnHelper.accessor('drug_description', {
      header: 'Drug',
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
  ];

  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  // Applied search is what's actually filtering the table (only updated on Enter)
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');
  // Store search results when search is applied (Enter pressed) so table doesn't change while typing more.
  // We can't rely solely on react-query's placeholderData because useSearchNotifications queries using searchValue
  // (what's being typed), not appliedSearch (what was submitted). When the user types more after pressing
  // Enter, searchResults updates for the new query but the table should keep showing the applied results.
  const [appliedSearchResults, setAppliedSearchResults] = useState<NotificationRow[]>([]);

  // Fetch search results from the server for the search preview dropdown
  // Note: searchValue is already debounced by TableSearchWithPreview (300ms)
  const { data: searchResults, isFetching: isSearching } = useSearchNotifications(searchValue);

  // When search is applied, use backend search results; otherwise use current data
  const tableData = appliedSearch ? appliedSearchResults : data;

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // These columns are used for filtering only, not displaying (closed items show indicator in date column instead)
    status: false,
    office_name: false,
    drug_description: false,
    // The selection column is only visible on the Open tab because you can't select completed notifications
    selection: searchParams.get('status') !== 'completed',
  });

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnFilters,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      columnVisibility,
    },
  });

  React.useEffect(() => {
    const searchFromParams = searchParams.get('search') || '';
    setSearchValue(searchFromParams);
    setAppliedSearch(searchFromParams);
    // Note: appliedSearchResults will be populated by the useSearchNotifications effect below

    const filterValues = notificationsFilterValuesFromSearchParams(searchParams);

    const prescriberValue = getPrescriberFilterValue(
      filterValues,
      filterOptions,
      defaultPrescriberNames,
      hasUserClearedFilters,
    );

    // When search is active, show results across all statuses; otherwise filter by current tab
    const statusFilterValue = searchFromParams ? 'all' : searchParams.get('status') || 'open';
    setColumnFilters([
      { id: 'status', value: statusFilterValue },
      {
        id: 'office_name',
        value: filterValues.office.length
          ? filterValues.office
          : hasUserClearedFilters.current
            ? []
            : officeCookieValues,
      },
      {
        id: 'prescriber_name',
        value: prescriberValue,
      },
      {
        id: 'drug_description',
        value: filterValues.drug_name.length
          ? filterValues.drug_name
          : hasUserClearedFilters.current
            ? []
            : drugCookieValues,
      },
      { id: 'notification', value: filterValues.type },
    ]);
  }, [searchParams, table, userEmail, defaultPrescriberNames, filterOptions, officeCookieValues, drugCookieValues]);

  // Sync columnVisibility with URL changes (e.g., browser back/forward)
  React.useEffect(() => {
    const newSelectionValue = (searchParams.get('status') || 'open') !== 'completed';
    setColumnVisibility((prev) => {
      if (prev.selection !== newSelectionValue) {
        return {
          ...prev,
          selection: newSelectionValue,
        };
      }
      return prev;
    });
  }, [searchParams]);

  // Populate appliedSearchResults when search results arrive for the applied search
  // This handles both initial page load with search param and subsequent searches
  React.useEffect(() => {
    if (appliedSearch && searchResults?.notifications && searchValue === appliedSearch) {
      setAppliedSearchResults(searchResults.notifications);
    }
  }, [appliedSearch, searchResults, searchValue]);

  const setStatus = React.useCallback(
    (status: string, e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      updateUrlSearchParams(searchParams, setSearchParams, { status });
      table.getColumn('status')!.setFilterValue(status);
    },
    [searchParams, setSearchParams, table],
  );

  const handleRowClick = (row: any) => {
    navigate(`/rxs/${row.rx_id}`);
  };

  const nullStateType = useTableNullState({
    columnFilters,
    filterIds: ['office_name', 'prescriber_name', 'notification', 'drug_description'],
    defaultNullStateType: getDefaultNullStateType(table.getColumn('status')?.getFilterValue() as string),
    globalFilter: appliedSearch,
  });

  // Clear search input and results
  const clearSearch = React.useCallback(() => {
    setSearchValue('');
    setAppliedSearch('');
    setAppliedSearchResults([]);
    updateUrlSearchParams(searchParams, setSearchParams, { search: undefined });
  }, [searchParams, setSearchParams]);

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters except status (which is controlled by URL)
    const currentStatus = searchParams.get('status') || 'open';
    setColumnFilters([{ id: 'status', value: currentStatus }]);

    // Clear search
    setSearchValue('');
    setAppliedSearch('');
    setAppliedSearchResults([]);

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'office_name');
    updateTableColumnFilterValueCookie([], authContext.user?.member_id || null, 'drug_name');

    // Clear TableMultiselectFilter refs
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
    typeFilterRef.current?.clear();
    drugFilterRef.current?.clear();

    // Mark that user has explicitly cleared filters
    hasUserClearedFilters.current = true;

    updateUrlSearchParams(searchParams, setSearchParams, {
      search: undefined,
      office: undefined,
      prescriber: undefined,
      drug_name: undefined,
      type: undefined,
    });
  }, [searchParams, setSearchParams, authContext.user?.member_id]);

  return (
    <TableContainer>
      <FilterBar>
        <TabsAndSearchRow>
          <TableTabList
            tabs={[
              { label: 'Open', value: 'open' },
              { label: 'Completed', value: 'completed' },
            ]}
            selectedValue={(table.getColumn('status')!.getFilterValue() as string) || 'open'}
            onSelect={(value) => {
              if (appliedSearch) {
                // Clear search when selecting a tab while search is active
                setSearchValue('');
                setAppliedSearch('');
                setAppliedSearchResults([]);
                updateUrlSearchParams(searchParams, setSearchParams, {
                  search: undefined,
                  status: value,
                });
                table.getColumn('status')!.setFilterValue(value);
              } else {
                setStatus(value);
              }
              setColumnVisibility((prev) => ({
                ...prev,
                selection: value === 'open',
              }));
            }}
            tabListId={statusTabId}
            tabPanelId={statusPanelId}
            ariaLabel="Filter notifications by status"
            {...createTrackingProps({ elementName: 'Notifications Table: Status Tabs', unmask: true })}
          />
          <TableSearchWithPreview
            tableName="Notifications Table"
            value={searchValue}
            onChange={setSearchValue}
            onApplySearch={(value) => {
              setAppliedSearch(value);
              // Use current search results if available
              // Only clear results when value is empty; if searchResults is temporarily unavailable, keep existing
              if (value) {
                if (searchResults?.notifications) {
                  setAppliedSearchResults(searchResults.notifications);
                }
                // If searchResults is unavailable, keep existing appliedSearchResults
                table.getColumn('status')?.setFilterValue('all');
              } else {
                setAppliedSearchResults([]);
              }
              updateUrlSearchParams(searchParams, setSearchParams, {
                search: value === '' ? undefined : value,
              });
            }}
            onClear={() => {
              setAppliedSearch('');
              setAppliedSearchResults([]);
              // Restore status filter to current tab
              table.getColumn('status')?.setFilterValue(searchParams.get('status') || 'open');
              updateUrlSearchParams(searchParams, setSearchParams, {
                search: undefined,
              });
            }}
            data={searchResults?.notifications || []}
            getItemKey={(notification) => notification.id}
            onPreviewItemClick={(notification) => navigate(`/rxs/${notification.rx_id}`)}
            renderPreviewItem={(notification) => (
              <NotificationPreviewItem notification={notification} searchTerm={searchValue} />
            )}
            isLoading={isSearching}
            minSearchLength={MIN_SEARCH_LENGTH}
          />
        </TabsAndSearchRow>

        {appliedSearch ? (
          <SearchResultsBanner
            searchTerm={appliedSearch}
            onClearSearch={clearSearch}
            count={table.getRowModel().rows?.length ?? 0}
          />
        ) : (
          <FilterBarRow>
            {selectedNotifications.length > 0 ? (
              <>
                <Button
                  size="sm"
                  onClick={handleMarkAsComplete}
                  disabled={closeNotificationsMutation.isPending}
                  {...createTrackingProps({ elementName: 'Notifications Table: Bulk Complete Button' })}
                >
                  Mark as complete
                </Button>
                <Button size="sm" variant="tertiary" onClick={() => setSelectedNotifications([])}>
                  Deselect
                </Button>
                <DividerPipe />
                <Typography>{selectedNotifications.length} selected</Typography>
              </>
            ) : (
              <>
                <TableMultiselectFilter
                  options={filterOptions?.offices || []}
                  label={'Office'}
                  initialValue={
                    getDropdownInitialValueFromSearchParamsAndCookie(
                      searchParams.get('office') ||
                        (hasUserClearedFilters.current ? '' : officeCookieValues.join(MULTI_SELECT_DELINEATOR)),
                      authContext.user?.member_id || null,
                      'office_name',
                      filterOptions?.offices,
                    ) as MultiselectAutocompleteOption[]
                  }
                  sortFn={sortOfficeNames}
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedValuesList = value.map((valueItem) => valueItem.value);

                    // Reset the cleared flag when user makes a manual selection
                    hasUserClearedFilters.current = false;

                    table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                    updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'office_name');
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      office: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                    });
                  }}
                  ref={officeFilterRef}
                  {...createTrackingProps({ elementName: 'Notifications Table: Office Filter', unmask: true })}
                />
                <TableMultiselectFilter
                  // Force remount when prescriber options load to ensure initialValue is applied correctly
                  key={filterOptions?.prescribers ? 'prescriber-loaded' : 'prescriber-loading'}
                  options={filterOptions?.prescribers || []}
                  label={'Prescriber'}
                  initialValue={
                    filterOptions?.prescribers
                      ? (getDropdownInitialValueFromSearchParams(
                          authContext.user?.default_filters,
                          searchParams.get('prescriber') || '',
                          'providers',
                          filterOptions.prescribers,
                        ) as MultiselectAutocompleteOption[])
                      : []
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedNamesList = value.map((valueItem) => valueItem.description);
                    const selectedNpisList = value.map((valueItem) => valueItem.value);

                    // Reset the cleared flag when user makes a manual selection
                    hasUserClearedFilters.current = false;

                    table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      prescriber: selectedNpisList.length ? selectedNpisList.join(MULTI_SELECT_DELINEATOR) : undefined,
                    });
                  }}
                  ref={prescriberFilterRef}
                  {...createTrackingProps({ elementName: 'Notifications Table: Prescriber Filter', unmask: true })}
                />
                <TableMultiselectFilter
                  options={filterOptions?.drugs || []}
                  label={'Drug'}
                  initialValue={
                    getDropdownInitialValueFromSearchParamsAndCookie(
                      searchParams.get('drug_name') ||
                        (hasUserClearedFilters.current ? '' : drugCookieValues.join(MULTI_SELECT_DELINEATOR)),
                      authContext.user?.member_id || null,
                      'drug_name',
                      filterOptions?.drugs,
                    ) as MultiselectAutocompleteOption[]
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedValuesList = value.map((valueItem) => valueItem.value);
                    hasUserClearedFilters.current = false;
                    table.getColumn('drug_description')!.setFilterValue(selectedValuesList);
                    updateTableColumnFilterValueCookie(value, authContext.user?.member_id || null, 'drug_name');
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      drug_name: selectedValuesList.length
                        ? selectedValuesList.join(MULTI_SELECT_DELINEATOR)
                        : undefined,
                    });
                  }}
                  ref={drugFilterRef}
                  {...createTrackingProps({ elementName: 'Notifications Table: Drug Filter', unmask: true })}
                />
                <TableMultiselectFilter
                  options={stringsToDropdownOptions(typeStrings)}
                  label={'Type'}
                  initialValue={
                    getDropdownInitialValueFromSearchParams(
                      null,
                      searchParams.get('type') || '',
                      undefined,
                      stringsToDropdownOptions(typeStrings),
                    ) as MultiselectAutocompleteOption[]
                  }
                  onChange={(_, value: MultiselectAutocompleteOption[]) => {
                    const selectedValuesList = value.map((valueItem) => valueItem.value);
                    table.getColumn('notification')!.setFilterValue(selectedValuesList);
                    updateUrlSearchParams(searchParams, setSearchParams, {
                      type: selectedValuesList.length ? selectedValuesList.join(MULTI_SELECT_DELINEATOR) : undefined,
                    });
                  }}
                  ref={typeFilterRef}
                  {...createTrackingProps({ elementName: 'Notifications Table: Type Filter', unmask: true })}
                />
                <SaveFiltersButton
                  $visible={showSaveButton}
                  onClick={() => setShowSaveModal(true)}
                  {...createTrackingProps({ elementName: 'Notifications Table: Save Filters Button' })}
                />
              </>
            )}

            <TableResultCount count={table.getRowModel().rows?.length ?? 0} />
          </FilterBarRow>
        )}
      </FilterBar>

      <Table role="tabpanel" id={statusPanelId} aria-labelledby={statusTabId}>
        <thead {...createTrackingProps({ unmask: true })}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHeader
                    key={header.id}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                    $width={header.column.getSize().toString()}
                    $sortable={header.column.getCanSort()}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <HeaderSortingIndicator
                        isSorted={header.column.getIsSorted()}
                        sortDirection={header.column.getIsSorted()}
                      />
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const isSelected = selectedNotifications.includes(row.original.id);

              return (
                <ClickableRow
                  $selected={row.original.id === notificationId || isSelected}
                  key={row.original.id}
                  onClick={() => handleRowClick(row.original)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleRowClick(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TopAlignedTableCell key={cell.id} $width={cell.column.getSize().toString()}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TopAlignedTableCell>
                    );
                  })}
                </ClickableRow>
              );
            })
          ) : (
            <tr>
              <TableCell colSpan={columns.length}>
                <TableNullState
                  nullStateType={nullStateType}
                  onClearFilters={clearAllFilters}
                  onClearSearch={clearSearch}
                  onAction={() => navigate('/settings?tab=notifications')}
                  tableName="Notifications Table"
                />
              </TableCell>
            </tr>
          )}
        </tbody>
      </Table>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </TableContainer>
  );
};

export default NotificationsTable;
